import axios, { type AxiosError } from 'axios';
import { notify } from '@/core/toast/notify';
import type { ApiResponse } from '@/types/api';
import { consumePermissionDeniedHandled } from '@/utils/moduleNavigation';

const CODE_HINTS: Record<string, string> = {
  PLAN_LIMIT_EXCEEDED:
    'Your subscription plan limit has been reached. Open Subscription to upgrade or request a custom plan.',
  SUBSCRIPTION_INACTIVE: 'Your subscription is inactive. Renew your plan under Subscription.',
  VALIDATION_ERROR: 'Please check the highlighted fields and try again.',
  RESOURCE_NOT_FOUND: 'The item you requested could not be found.',
  DUPLICATE_RESOURCE: 'This record already exists. Use a different code or name.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  TENANT_VIOLATION: 'You cannot access data from another organization.',
  UNAUTHORIZED: 'Please sign in again to continue.',
  INVALID_TOKEN: 'Your session expired. Please sign in again.',
  BUSINESS_ERROR: 'This action is not allowed in the current state.',
  INTERNAL_ERROR: 'Something went wrong on our side. Please try again in a moment.',
};

function humanizeField(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^\w/, (c) => c.toUpperCase());
}

function formatValidationErrors(error: unknown): string | null {
  if (!error || typeof error !== 'object' || Array.isArray(error)) return null;
  const entries = Object.entries(error as Record<string, unknown>).filter(
    ([, v]) => v != null && String(v).trim() !== '',
  );
  if (entries.length === 0) return null;
  return entries
    .map(([field, value]) => {
      if (field === 'code') return null;
      return `${humanizeField(field)}: ${String(value)}`;
    })
    .filter(Boolean)
    .join(' · ');
}

function extractCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || Array.isArray(error)) return undefined;
  const code = (error as Record<string, unknown>).code;
  return typeof code === 'string' ? code : undefined;
}

function messageFromApiBody(body: ApiResponse<unknown> | undefined, status?: number): string | null {
  if (!body) return null;

  const validation = formatValidationErrors(body.error);
  if (validation) return validation;

  if (typeof body.error === 'string' && body.error.trim()) return body.error.trim();

  const rawMessage = body.message?.trim();
  if (rawMessage?.includes('Feature not enabled:')) {
    return rawMessage;
  }

  const code = extractCode(body.error);
  if (body.message && body.message.trim() && body.message !== 'Success') {
    const generic = ['Validation failed', 'Business rule violation', 'Resource not found', 'Internal server error'];
    if (!generic.includes(body.message) || !code) {
      return body.message.trim();
    }
  }

  if (code && CODE_HINTS[code]) return CODE_HINTS[code];

  if (status === 402) return CODE_HINTS.PLAN_LIMIT_EXCEEDED;
  if (status === 403) return CODE_HINTS.FORBIDDEN;
  if (status === 404) return CODE_HINTS.RESOURCE_NOT_FOUND;

  return body.message?.trim() || null;
}

export class ApiError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly apiMessage: string;

  constructor(message: string, options?: { status?: number; code?: string; cause?: unknown }) {
    super(message);
    this.name = 'ApiError';
    this.apiMessage = message;
    this.status = options?.status;
    this.code = options?.code;
    if (options?.cause) this.cause = options.cause;
  }

  static fromResponse(body: ApiResponse<unknown>, status?: number): ApiError {
    const message =
      messageFromApiBody(body, status) ?? 'Request failed. Please try again.';
    return new ApiError(message, { status, code: extractCode(body.error) });
  }

  static fromUnknown(err: unknown, fallback = 'Something went wrong. Please try again.'): ApiError {
    if (err instanceof ApiError) return err;

    if (axios.isAxiosError(err)) {
      const ax = err as AxiosError<ApiResponse<unknown>>;
      const body = ax.response?.data;
      const status = ax.response?.status;
      if (body) return ApiError.fromResponse(body, status);
      if (ax.code === 'ECONNABORTED') {
        return new ApiError('Request timed out. Please try again.', { status, cause: err });
      }
      if (!ax.response) {
        return new ApiError(
          'Cannot reach the server. Make sure the backend is running and your network is connected.',
          { cause: err },
        );
      }
      if (status === 401) return new ApiError(CODE_HINTS.UNAUTHORIZED, { status, cause: err });
      if (status === 403) return new ApiError(CODE_HINTS.FORBIDDEN, { status, cause: err });
      if (status === 404) return new ApiError(CODE_HINTS.RESOURCE_NOT_FOUND, { status, cause: err });
      if (status === 402) return new ApiError(CODE_HINTS.PLAN_LIMIT_EXCEEDED, { status, cause: err });
      if (status && status >= 500) {
        return new ApiError(CODE_HINTS.INTERNAL_ERROR, { status, cause: err });
      }
    }

    if (err instanceof Error && err.message.trim()) {
      return new ApiError(err.message, { cause: err });
    }

    return new ApiError(fallback, { cause: err });
  }
}

/** User-facing message from any API or thrown error. */
export function getApiErrorMessage(err: unknown, fallback?: string): string {
  return ApiError.fromUnknown(err, fallback).message;
}

export function getApiErrorStatus(err: unknown): number | undefined {
  if (err instanceof ApiError) return err.status;
  if (axios.isAxiosError(err)) return err.response?.status;
  return undefined;
}

/** Show a toast with the best available backend error message. */
export function showApiError(err: unknown, fallback?: string): void {
  const status = getApiErrorStatus(err);
  if (status === 403 && consumePermissionDeniedHandled()) {
    return;
  }
  notify.error(getApiErrorMessage(err, fallback));
}
